import useBiteClassification from 'hooks/useBiteClassification'
import { isCompleteBiteClassification } from 'utils/biteClassificationUtils'
import Actions from 'actions'
import { AsyncStatus } from 'consts'
import { ScanTrackingStatus, TrackingConditions, TrackingConditionsTypes, TreatmentTrackOptions } from 'consts/giConsts'
import useHITagging from 'hooks/useHITagging'
import useRolePermissions from 'hooks/useRolePermissions'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { sortTeeth } from 'utils/giUtils'
import { logWarn } from 'utils/logUtils'
import { splitAndSortConditionsByTreatmentType } from './treatmentTrackingUtils'
import useGaFlags from 'hooks/useGaFlags'

export default ({ scanId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { getTrackingConditions } = useHITagging()
  const { AIForHI } = useGaFlags()
  const {
    isEnabled: biteClassificationEnabled,
    isLoading: biteFlagLoading,
    isError: biteFlagError
  } = useBiteClassification()
  const [biteClassification, setBiteClassification] = useState({})

  const trackingCondition = getTrackingConditions()

  const { status: submissionStatus } = useSelector(state => state.giReducer.scanTracking)
  const { grinScans, treatment } = useSelector(state => state.treatmentReducer)

  const [trackStatus, setTrackStatus] = useState('')
  const [notes, setNotes] = useState('')
  const [isDismissed, setIsDismissed] = useState(null)
  const [selectedConditions, setSelectedConditions] = useState({})
  const [irrelevantConditionOptions, setIrrelevantConditionOptions] = useState([])

  const conditionOptions = useMemo(() => {
    const options = Object.values(trackingCondition).filter(
      condition =>
        condition.id !== TrackingConditionsTypes.AlignerUnseating && condition.trackingStatus.includes(trackStatus)
    )

    if (AIForHI) {
      const { relevantConditions, rest } = splitAndSortConditionsByTreatmentType({
        conditions: options,
        treatmentType: treatment?.type
      })

      setIrrelevantConditionOptions(rest)
      return relevantConditions
    }

    return options
  }, [AIForHI, trackStatus, trackingCondition, treatment?.type])

  const { isGiUser } = useRolePermissions()
  const giTrackSettings = useMemo(() => JSON.parse(treatment?.giTrackSettings || '{}'), [treatment])
  const untrackedTeeth = useMemo(() => sortTeeth(giTrackSettings?.untrackedTeeth), [giTrackSettings])
  const grinScan = useMemo(() => grinScans?.find(scan => scan.id === scanId), [scanId, grinScans])
  const grinScanForTagging = useMemo(() => {
    if (!grinScan?.scanPairId) {
      return grinScan
    }

    return grinScans?.find(scan => scan.scanPairId === grinScan.scanPairId && scan.withAligner)
  }, [grinScans, grinScan])

  const isLoading = useMemo(() => submissionStatus === AsyncStatus.Loading, [submissionStatus])
  const wasTrackingDataSubmittedBefore = useMemo(
    () => !!grinScanForTagging?.trackingStatus && grinScanForTagging?.trackingStatus !== ScanTrackingStatus.Pending,
    [grinScanForTagging]
  )
  const isNoteEnabled = useMemo(
    () =>
      Object.keys(selectedConditions).find(
        conditionType => selectedConditions[conditionType] && TrackingConditions[conditionType]?.withNote
      ),
    [selectedConditions]
  )
  const isSubmitDisabled = useMemo(() => {
    return (
      submissionStatus === AsyncStatus.Loading ||
      !grinScanForTagging?.id ||
      !trackStatus ||
      biteFlagLoading ||
      (biteClassificationEnabled && !isCompleteBiteClassification(biteClassification)) ||
      (trackStatus === TreatmentTrackOptions.OffTrack && isEmpty(selectedConditions)) ||
      (isNoteEnabled && !notes)
    )
  }, [
    trackStatus,
    submissionStatus,
    selectedConditions,
    isNoteEnabled,
    notes,
    biteFlagLoading,
    biteClassificationEnabled,
    biteClassification,
    grinScanForTagging?.id
  ])

  const selectedConditionDetails = Object.keys(selectedConditions || {}).map(conditionId => {
    const conditionSummary = t(`dialogs.scanSummary.treatmentTracking.conditions.${conditionId}`)

    if (!isEmpty(selectedConditions?.[conditionId]?.selections)) {
      return `${conditionSummary} (${selectedConditions[conditionId].selections.join(', ')})`
    }

    return conditionSummary
  }, {})

  const setConditionEnabled = useCallback(
    ({ conditionId, checked }) => {
      if (conditionId === TrackingConditionsTypes.Other && !checked) {
        setNotes('')
      }
      setSelectedConditions({ ...selectedConditions, [conditionId]: checked ? { selections: [] } : undefined })
    },
    [selectedConditions]
  )

  const setConditionSelections = useCallback(
    ({ conditionId, selections = [] }) => {
      setSelectedConditions({
        ...selectedConditions,
        [conditionId]: { ...selectedConditions[conditionId], selections }
      })
    },
    [selectedConditions]
  )

  const resetChanges = useCallback(() => {
    const trackingDetails = JSON.parse(grinScanForTagging?.trackingDetails || '{}')
    setTrackStatus(trackingDetails.trackStatus)
    setNotes(trackingDetails.notes)
    setSelectedConditions(trackingDetails.conditions || {})
    setIsDismissed(trackingDetails.isDismissed)
    setBiteClassification(trackingDetails.biteClassification || {})
  }, [grinScanForTagging?.trackingDetails])

  const submit = useCallback(() => {
    if (isSubmitDisabled) return
    const scanSummaryData = JSON.parse(grinScan?.scanSummaryData || '{}')
    const timeToTag = moment().diff(moment(scanSummaryData.finishedAt), 'minute')

    trackEvent(`${isGiUser ? 'GI' : 'HI'} - review done click`, {
      status: trackStatus,
      patientId: treatment?.patientId,
      scanId,
      notes,
      reasons: Object.keys(selectedConditions),
      timeToTag
    })

    dispatch(
      Actions.submitScanTrackingData({
        scanId: grinScanForTagging.id,
        trackStatus,
        notes,
        isEditing: wasTrackingDataSubmittedBefore,
        conditions: selectedConditions,
        ...(biteClassificationEnabled ? { biteClassification } : {}),
        isGiUser
      })
    )
  }, [
    grinScan,
    isGiUser,
    trackStatus,
    treatment,
    scanId,
    notes,
    selectedConditions,
    dispatch,
    grinScanForTagging,
    wasTrackingDataSubmittedBefore,
    isSubmitDisabled,
    biteClassificationEnabled,
    biteClassification
  ])

  const dismiss = useCallback(() => {
    if (isGiUser) {
      logWarn('Tried to dismiss scan tracking with GI user')
      return
    }

    trackEvent('HI - review dismiss clicked', {
      patientId: treatment?.patientId,
      scanId
    })

    dispatch(
      Actions.submitScanTrackingData({
        scanId: grinScanForTagging.id,
        trackStatus: 'not-required',
        dismissScan: true,
        isEditing: wasTrackingDataSubmittedBefore,
        isGiUser: false
      })
    )
  }, [scanId, dispatch, treatment, isGiUser, wasTrackingDataSubmittedBefore, grinScanForTagging])

  useEffect(() => {
    const trackingDetails = JSON.parse(grinScanForTagging?.trackingDetails || '{}')
    setTrackStatus(trackingDetails.trackStatus)
    setNotes(trackingDetails.notes)
    setSelectedConditions(trackingDetails.conditions || {})
    setIsDismissed(trackingDetails.isDismissed)
    setBiteClassification(trackingDetails.biteClassification || {})
  }, [grinScanForTagging?.id, grinScanForTagging?.trackingDetails])

  useEffect(() => {
    const trackingDetails = JSON.parse(grinScanForTagging?.trackingDetails || '{}')
    setSelectedConditions(trackingDetails?.conditions || {})
  }, [trackStatus, grinScan, grinScanForTagging])

  return {
    biteClassificationEnabled,
    biteFlagError,
    scanTrackingStatus: grinScanForTagging?.trackingStatus,
    isSubmitDisabled,
    isLoading,
    conditionOptions,
    irrelevantConditionOptions,
    giTrackSettings,
    untrackedTeeth,
    isNoteEnabled,
    isDismissed,
    wasTrackingDataSubmittedBefore,
    setConditionEnabled,
    setConditionSelections,
    submit,
    dismiss,
    resetChanges,
    selectedConditionDetails,
    values: {
      biteClassification,
      setBiteClassification,
      trackStatus,
      setTrackStatus,
      notes,
      setNotes,
      conditions: selectedConditions,
      setConditions: setSelectedConditions
    }
  }
}
